import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircleOutlined, EyeOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { RBACService, PERMISSIONS } from '../utils/rbac';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { GoodsReceived, GoodsReceivedCreate, PurchaseOrder } from '../types/purchaseOrder';
import ModernFormModal from '../components/ModernFormModal';
import './GRPO.css';

const { Title, Text } = Typography;
const { Option } = Select;

type GrpoLine = {
  key: string;
  po_item_id: number;
  product_id?: number;
  product_name: string;
  ordered_quantity: number;
  received_quantity: number;
  unit_price: number;
  manufacture_date?: string;
  expiry_date?: string;
};

const GRPO: React.FC = () => {
  const canReceive = RBACService.hasPermission(PERMISSIONS.GOODS_RECEIVE);
  const canComplete = RBACService.hasPermission(PERMISSIONS.GOODS_RECEIVE_COMPLETE);

  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<PurchaseOrder[]>([]);
  const [records, setRecords] = useState<GoodsReceived[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewGrpoRecord, setViewGrpoRecord] = useState<GoodsReceived | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [lines, setLines] = useState<GrpoLine[]>([]);
  const [form] = Form.useForm();

  const nav = useNavigate();
  const location = useLocation();

  const poIdFromQuery = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const id = Number(p.get('po_id') || '');
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [location.search]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        purchaseOrderService.getPendingReceipts({ limit: 200 }),
        purchaseOrderService.getGoodsReceived({ limit: 200 }),
      ]);
      setPending(p || []);
      setRecords(r || []);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to load GRPO data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Deep-link: /grpo?po_id=123 opens create modal for that PO
  useEffect(() => {
    if (!poIdFromQuery) return;
    const po = pending.find(x => x.id === poIdFromQuery);
    if (po) {
      openCreate(po);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poIdFromQuery, pending]);

  const openCreate = (po: PurchaseOrder) => {
    if (!canReceive) {
      message.error('You do not have permission to receive goods');
      return;
    }
    setSelectedPO(po);
    const poLines: GrpoLine[] = (po.items || []).map((it, idx) => ({
      key: String(it.id ?? idx),
      po_item_id: it.id as number,
      product_id: it.product_id ?? undefined,
      product_name:
        it.vendor_product?.name || it.product?.name || (it.product_id != null ? String(it.product_id) : 'Vendor line'),
      ordered_quantity: it.quantity,
      received_quantity: Math.max(0, (it.quantity || 0) - (it.received_quantity || 0)),
      unit_price: Number(it.unit_price || 0),
    }));
    setLines(poLines);
    form.resetFields();
    form.setFieldsValue({
      received_date: moment(),
    });
    setCreateOpen(true);
  };

  // Open GRPO modal without selecting a PO yet (user can pick a PO inside modal)
  const openCreatePicker = () => {
    if (!canReceive) {
      message.error('You do not have permission to receive goods');
      return;
    }
    setSelectedPO(null);
    setLines([]);
    form.resetFields();
    form.setFieldsValue({
      received_date: moment(),
    });
    setCreateOpen(true);
  };

  const createGrpo = async (alsoComplete: boolean) => {
    if (!selectedPO?.id) return;
    setSubmitting(true);
    try {
      const values = await form.validateFields();
      const missingDates = lines.find(
        (l) => l.received_quantity > 0 && (!l.manufacture_date || !l.expiry_date)
      );
      if (missingDates) {
        message.error(`Enter manufacture and expiry dates for ${missingDates.product_name}`);
        setSubmitting(false);
        return;
      }
      const badOrder = lines.find(
        (l) =>
          l.received_quantity > 0 &&
          l.manufacture_date &&
          l.expiry_date &&
          l.expiry_date <= l.manufacture_date
      );
      if (badOrder) {
        message.error(`Expiry must be after manufacture for ${badOrder.product_name}`);
        setSubmitting(false);
        return;
      }
      const payload: GoodsReceivedCreate = {
        po_id: selectedPO.id,
        receipt_number: values.receipt_number || undefined,
        invoice_number: values.invoice_number || undefined,
        notes: values.notes || undefined,
        received_date: values.received_date.format('YYYY-MM-DD'),
        items: lines.map((l) => ({
          po_item_id: l.po_item_id,
          ...(l.product_id != null ? { product_id: l.product_id } : {}),
          ordered_quantity: l.ordered_quantity,
          received_quantity: l.received_quantity,
          unit_price: l.unit_price,
          manufacture_date: l.manufacture_date,
          expiry_date: l.expiry_date,
        })),
      };

      const created = await purchaseOrderService.createGoodsReceived(payload);
      message.success(`GRPO created (${created.grpo_number || created.id})`);

      if (alsoComplete && canComplete && created.id) {
        await purchaseOrderService.completeGoodsReceived(created.id);
        message.success('GRPO completed and inventory updated');
      }

      setCreateOpen(false);
      setSelectedPO(null);
      setLines([]);
      // remove po_id query if present
      nav('/grpo', { replace: true });
      refresh();
    } catch (err: any) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.detail || err?.message || 'Failed to create GRPO');
    } finally {
      setSubmitting(false);
    }
  };

  const completeGrpo = async (grpo: GoodsReceived) => {
    if (!canComplete) return;
    try {
      await purchaseOrderService.completeGoodsReceived(grpo.id as number);
      message.success('GRPO completed');
      refresh();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to complete GRPO');
    }
  };

  const viewGrpo = async (grpo: GoodsReceived) => {
    try {
      const full = await purchaseOrderService.getGoodsReceivedById(grpo.id as number);
      setViewGrpoRecord(full);
      setViewOpen(true);
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Failed to load GRPO details');
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const pendingColumns: ColumnsType<PurchaseOrder> = [
    { title: 'PO #', dataIndex: 'po_number', key: 'po_number', width: 140 },
    {
      title: 'Vendor',
      key: 'vendor',
      render: (_: any, po) => po.vendor?.name || '-',
    },
    {
      title: 'Location',
      key: 'location',
      render: (_: any, po) => po.location?.name || '-',
    },
    { title: 'Order Date', dataIndex: 'order_date', key: 'order_date', width: 140 },
    {
      title: 'Action',
      key: 'action',
      width: 160,
      render: (_: any, po) => (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={!canReceive}
          onClick={() => openCreate(po)}
        >
          Create GRPO
        </Button>
      ),
    },
  ];

  const recordColumns: ColumnsType<GoodsReceived> = [
    { title: 'GRPO #', dataIndex: 'grpo_number', key: 'grpo_number', ellipsis: true },
    {
      title: 'PO #',
      key: 'po',
      ellipsis: true,
      render: (_: any, r) => r.purchase_order?.po_number || r.po_id,
    },
    {
      title: 'Received Date',
      dataIndex: 'received_date',
      key: 'received_date',
      width: 140,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (s: string) => (
        <Badge
          status={s === 'COMPLETED' ? 'success' : 'processing'}
          text={s === 'COMPLETED' ? 'Received' : 'Pending'}
        />
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 150,
      align: 'right',
      render: (v: any) => `Nu. ${Number(v || 0).toLocaleString()}`,
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      align: 'right',
      render: (_: any, r) => (
        <Space>
          {canComplete && r.status !== 'COMPLETED' && (
            <Button
              icon={<CheckCircleOutlined />}
              onClick={() => completeGrpo(r)}
            >
              Complete
            </Button>
          )}
          <Button
            icon={<EyeOutlined />}
            onClick={() => viewGrpo(r)}
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  const lineColumns: ColumnsType<GrpoLine> = [
    { title: 'Product', dataIndex: 'product_name', key: 'product_name' },
    { title: 'Ordered', dataIndex: 'ordered_quantity', key: 'ordered_quantity', width: 110 },
    {
      title: 'Receive Qty',
      dataIndex: 'received_quantity',
      key: 'received_quantity',
      width: 200,
      render: (v: number, line) => {
        const maxQty = line.ordered_quantity || 0;
        const current = typeof v === 'number' ? v : 0;

        const updateQuantity = (nextVal: number) => {
          const clamped = Math.max(0, Math.min(maxQty, Number.isFinite(nextVal) ? nextVal : 0));
          setLines(prev =>
            prev.map(x =>
              x.key === line.key ? { ...x, received_quantity: clamped } : x
            )
          );
        };

        return (
          <div className="grpo-qty-cell">
            <Button
              className="grpo-qty-btn"
              size="small"
              icon={<MinusOutlined />}
              disabled={current <= 0}
              onClick={() => updateQuantity(current - 1)}
            />
            <InputNumber
              className="grpo-qty-input"
              min={0}
              max={maxQty}
              value={current}
              onChange={(next) => updateQuantity(Number(next || 0))}
            />
            <Button
              className="grpo-qty-btn"
              size="small"
              icon={<PlusOutlined />}
              disabled={current >= maxQty}
              onClick={() => updateQuantity(current + 1)}
            />
          </div>
        );
      },
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      render: (v: number) => `Nu. ${Number(v || 0).toLocaleString()}`,
    },
    {
      title: 'Manufacture',
      dataIndex: 'manufacture_date',
      key: 'manufacture_date',
      width: 160,
      render: (v: string | undefined, line) => (
        <DatePicker
          value={v ? moment(v, 'YYYY-MM-DD') : null}
          format="DD MMM YYYY"
          style={{ width: '100%' }}
          onChange={(next) =>
            setLines((prev) =>
              prev.map((x) =>
                x.key === line.key
                  ? { ...x, manufacture_date: next ? next.format('YYYY-MM-DD') : undefined }
                  : x
              )
            )
          }
        />
      ),
    },
    {
      title: 'Expiry',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      width: 160,
      render: (v: string | undefined, line) => (
        <DatePicker
          value={v ? moment(v, 'YYYY-MM-DD') : null}
          format="DD MMM YYYY"
          style={{ width: '100%' }}
          onChange={(next) =>
            setLines((prev) =>
              prev.map((x) =>
                x.key === line.key
                  ? { ...x, expiry_date: next ? next.format('YYYY-MM-DD') : undefined }
                  : x
              )
            )
          }
        />
      ),
    },
  ];

  if (!canReceive && !canComplete) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={3}>GRPO</Title>
        <div>You do not have permission to access GRPO.</div>
      </div>
    );
  }

  return (
    <div className="grpo-page" style={{ minHeight: '100dvh' }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={2} style={{ marginBottom: 0, fontSize: '12px', fontWeight: 400 }}>GRPO (Goods Received)</Title>
        <Text type="secondary" style={{ fontSize: '12px' }}>Create and complete receipts separately from Purchase Orders</Text>
      </div>

      <Card
        className="page-table-card page-table-card--teal grpo-table-card"
        extra={
          canReceive ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreatePicker}>
              Create GRPO
            </Button>
          ) : null
        }
        loading={loading}
      >
        <Table
          className="grpo-records-table"
          columns={recordColumns}
          dataSource={records}
          rowKey="id"
          tableLayout="fixed"
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        />
      </Card>

      <ModernFormModal
        open={createOpen}
        onClose={() => {
          if (submitting) return;
          setCreateOpen(false);
          setSelectedPO(null);
          setLines([]);
          nav('/grpo', { replace: true });
        }}
        title={selectedPO?.po_number ? `Create GRPO - ${selectedPO.po_number}` : 'Create GRPO'}
        subtitle={
          selectedPO?.po_number
            ? 'Enter received quantities and receipt details below.'
            : 'Select a Purchase Order and enter received quantities.'
        }
        headerVariant="light"
        headerSummary={selectedPO ? (
          <>
            <div><span className="mf-summary-label">PO Number:</span> <span className="mf-summary-value">{selectedPO.po_number}</span></div>
            <div><span className="mf-summary-label">Vendor:</span> <span className="mf-summary-value">{selectedPO.vendor?.name ?? '—'}</span></div>
            <div><span className="mf-summary-label">Location:</span> <span className="mf-summary-value">{selectedPO.location?.name ?? '—'}</span></div>
          </>
        ) : null}
        getContainer={() => document.querySelector('.main-content-full') || document.body}
        wrapClassName="form-panel-full-wrap"
        width="100%"
        style={{ maxWidth: 'none' }}
        bodyStyle={{ padding: 0 }}
        cancelText="Cancel"
        okText={canComplete ? 'Create & Complete' : 'Create GRPO'}
        onOk={() => createGrpo(!!canComplete)}
        isSubmitting={submitting}
        destroyOnClose={false}
      >
        <div style={{ padding: 24, width: '100%', boxSizing: 'border-box' }}>
          {canComplete && selectedPO && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <Button onClick={() => createGrpo(false)} disabled={submitting}>
                Create Only
              </Button>
            </div>
          )}

          <Form form={form} layout="vertical" autoComplete="off" style={{ width: '100%' }}>
            {!selectedPO && (
              <Form.Item
                label="Purchase Order"
                required
                help="Select a Purchase Order to receive goods."
              >
                <Select
                  showSearch
                  placeholder="Select PO"
                  optionFilterProp="children"
                  onChange={(poId: number) => {
                    const po = pending.find(p => p.id === poId);
                    if (po) openCreate(po);
                  }}
                >
                  {pending.map((po) => (
                    <Option key={po.id} value={po.id as number}>
                      {po.po_number} — {po.vendor?.name || 'Vendor'} ({po.location?.name || 'Location'})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item name="receipt_number" label="Receipt Number">
                  <Input placeholder="Optional" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="invoice_number" label="Invoice Number">
                  <Input placeholder="Optional" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  name="received_date"
                  label="GRPO Received Date"
                  help="Date when goods were received (GRPO date)."
                  rules={[{ required: true, message: 'GRPO received date is required' }]}
                >
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={2} placeholder="Optional" />
            </Form.Item>

            <Divider orientation="left">Items</Divider>
            <Table
              columns={lineColumns}
              dataSource={lines}
              rowKey="key"
              pagination={false}
              size="small"
              scroll={{ x: 1100 }}
              locale={{
                emptyText: selectedPO ? 'No items found for this PO.' : 'Select a Purchase Order to load items.',
              }}
            />
          </Form>
        </div>
      </ModernFormModal>

      <Modal
        title={`GRPO - ${viewGrpoRecord?.grpo_number || 'Details'}`}
        open={viewOpen}
        onCancel={() => { setViewOpen(false); setViewGrpoRecord(null); }}
        footer={[
          <Button key="close" onClick={() => { setViewOpen(false); setViewGrpoRecord(null); }}>
            Close
          </Button>,
        ]}
        width={960}
      >
        {viewGrpoRecord && (
          <div>
            <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
              <Col span={8}><Text type="secondary">PO #</Text><br /><Text strong>{viewGrpoRecord.purchase_order?.po_number || viewGrpoRecord.po_id}</Text></Col>
              <Col span={8}><Text type="secondary">Received Date</Text><br /><Text strong>{viewGrpoRecord.received_date}</Text></Col>
              <Col span={8}><Text type="secondary">Status</Text><br /><Badge status={viewGrpoRecord.status === 'COMPLETED' ? 'success' : 'processing'} text={viewGrpoRecord.status === 'COMPLETED' ? 'Received' : 'Pending'} /></Col>
            </Row>
            <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
              <Col span={8}><Text type="secondary">Total</Text><br /><Text strong>Nu. {Number(viewGrpoRecord.total_amount || 0).toLocaleString()}</Text></Col>
              {viewGrpoRecord.receipt_number && <Col span={8}><Text type="secondary">Receipt #</Text><br /><Text strong>{viewGrpoRecord.receipt_number}</Text></Col>}
              {viewGrpoRecord.invoice_number && <Col span={8}><Text type="secondary">Invoice #</Text><br /><Text strong>{viewGrpoRecord.invoice_number}</Text></Col>}
            </Row>
            {(viewGrpoRecord.items?.length ?? 0) > 0 && (
              <>
                <Divider orientation="left">Items</Divider>
                <Table
                  dataSource={viewGrpoRecord.items}
                  rowKey={(r) => String(r.id ?? r.po_item_id)}
                  pagination={false}
                  size="small"
                  scroll={{ x: 900 }}
                  columns={[
                    { title: 'Product', dataIndex: ['product', 'name'], key: 'product', render: (_: any, r) => r.product?.name || `Product ${r.product_id}` },
                    { title: 'Ordered', dataIndex: 'ordered_quantity', key: 'ordered_quantity', width: 90 },
                    { title: 'Received', dataIndex: 'received_quantity', key: 'received_quantity', width: 90 },
                    { title: 'Unit Price', dataIndex: 'unit_price', key: 'unit_price', width: 110, render: (v: number) => `Nu. ${Number(v || 0).toLocaleString()}` },
                    { title: 'Manufacture', dataIndex: 'manufacture_date', key: 'manufacture_date', width: 120, render: (v: string) => v || '—' },
                    { title: 'Expiry', dataIndex: 'expiry_date', key: 'expiry_date', width: 120, render: (v: string) => v || '—' },
                    { title: 'Total', dataIndex: 'total_price', key: 'total_price', width: 110, render: (v: number) => `Nu. ${Number(v || 0).toLocaleString()}` },
                  ]}
                />
              </>
            )}
            {viewGrpoRecord.notes && (
              <>
                <Divider orientation="left">Notes</Divider>
                <Text>{viewGrpoRecord.notes}</Text>
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GRPO;

